/**
 * Hook that returns an inline style object to breathe icon color
 * between green-700 and green-400. Uses inline style to bypass
 * global SVG CSS rules that override fill/stroke via currentColor.
 */

import { useState, useEffect } from 'react';
import type { CSSProperties } from 'react';

export const useBreathingGreen = (active: boolean): CSSProperties => {
  const [phase, setPhase] = useState(0); // 0..100

  useEffect(() => {
    if (!active) return;
    let frame: number;
    const start = performance.now();
    const duration = 2000; // 2s full cycle
    const tick = (now: number) => {
      const elapsed = (now - start) % duration;
      // sine wave 0→1→0 over duration
      const t = (Math.sin((elapsed / duration) * Math.PI * 2 - Math.PI / 2) + 1) / 2;
      setPhase(t * 100);
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [active]);

  if (!active) return {};

  // Interpolate between green-700 rgb(21,128,61) and green-400 rgb(74,222,128)
  const t = phase / 100;
  const r = Math.round(21 + (74 - 21) * t);
  const g = Math.round(128 + (222 - 128) * t);
  const b = Math.round(61 + (128 - 61) * t);
  const color = `rgb(${r},${g},${b})`;

  return { color, fill: color, stroke: color };
};
